import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { IUnmetContractRequirement } from '@/store/types';

import { Table } from '@/components/common';
import { ContractRequirementStatus } from '@/store/enums';
import { parseSearchParams } from '@/helpers';
import { colors, otherColors } from '@/styles/colors';
import routes from '@/config/routes';
import { ProgressBar, StyledButton, TableCell } from './ContractsRequirements.styled';
import { useWeek } from '@/hooks';
import { displayDeadline, formatWeekRange } from './helpers';

const tableConfig = {
  labelGig: { width: '0 0 30%' },
  labelWeek: { width: '1 0 100px' },
  labelType: { width: '1 0 10%' },
  labelHoursNotMet: { width: '0 0 150px' },
  labelDeadline: { width: '1 0 100px' },
  button: { width: '1 0 110px' },
  spacer: { width: '0 0 50px' },
};

interface IProps {
  paginatedData: IUnmetContractRequirement[][];
  page: number;
}

const ContractsRequirementsDesktopTable = ({ paginatedData, page }: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const { getWeek } = useWeek();

  return (
    <Table minWidth={900}>
      <Table.Header>
        <Table.HeaderCell flex={tableConfig.labelGig.width}>{t`dashboard.gigRequirements.labelGig`}</Table.HeaderCell>
        <Table.HeaderCell flex={tableConfig.labelWeek.width}>{t`dashboard.gigRequirements.labelWeek`}</Table.HeaderCell>
        <Table.HeaderCell flex={tableConfig.labelType.width}>{t`dashboard.gigRequirements.labelType`}</Table.HeaderCell>
        <Table.HeaderCell
          flex={tableConfig.labelHoursNotMet.width}
        >{t`dashboard.gigRequirements.labelHoursNotMet`}</Table.HeaderCell>
        <Table.HeaderCell
          flex={tableConfig.labelDeadline.width}
        >{t`dashboard.gigRequirements.labelDeadline`}</Table.HeaderCell>
        <Table.HeaderCell flex={tableConfig.button.width}></Table.HeaderCell>
        <Table.HeaderCell flex={tableConfig.spacer.width}></Table.HeaderCell>
      </Table.Header>
      {paginatedData[page]?.map((contract, id) => {
        const week = getWeek(contract.weekId);

        return (
          <Table.TableRow
            key={`${contract.requirementLabel}${contract.contractId}${contract.weekId}${id}`}
            firstBgColor={colors.gray.c50}
            secondBgColor={otherColors.white}
          >
            <TableCell flex={tableConfig.labelGig.width} multiline={2}>
              {contract.jobLabel}
            </TableCell>
            <TableCell flex={tableConfig.labelWeek.width}>{week && formatWeekRange(week)}</TableCell>
            <TableCell flex={tableConfig.labelType.width}>{contract.requirementLabel}</TableCell>
            <TableCell flex={tableConfig.labelHoursNotMet.width}>
              {t('dashboard.gigRequirements.textHoursNotMet', {
                max: contract.unmet.max,
                now: contract.unmet.now,
              })}
              <ProgressBar
                max={contract.unmet.max}
                now={contract.unmet.now}
                variant={ContractRequirementStatus.Danger}
              />
            </TableCell>
            <TableCell flex={tableConfig.labelDeadline.width}>{displayDeadline(contract)}</TableCell>
            <TableCell flex={tableConfig.button.width}>
              <Link
                to={{
                  pathname: routes.sp.scheduling.mySchedule.index,
                  search: parseSearchParams({ weekId: contract.weekId, showAvailable: true }),
                }}
              >
                <StyledButton
                  size="s"
                  color="redOutline"
                >{t`dashboard.gigRequirements.btnPickMoreShifts`}</StyledButton>
              </Link>
            </TableCell>
            <TableCell flex={tableConfig.spacer.width}></TableCell>
          </Table.TableRow>
        );
      })}
    </Table>
  );
};

export { ContractsRequirementsDesktopTable };
